import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import SeoScript from "@/components/feature/SeoScript";
import AnimeNavbar from "@/components/feature/AnimeNavbar";
import AnimeFooter from "@/components/feature/AnimeFooter";
import usePageTitle from "@/hooks/usePageTitle";
import { siteName } from "@/mocks/anime";

const heroBg =
  "https://readdy.ai/api/search-image?query=warm%20anime%20illustration%20of%20a%20desk%20with%20a%20laptop%2C%20open%20notebooks%20and%20a%20steaming%20cup%20by%20a%20window%20with%20evening%20light%2C%20large%20dark%20amber%20and%20brown%20areas%20for%20text%20contrast%2C%20ink%20wash%20style%2C%20cozy%20creator%20workspace%20manga%20aesthetic%2C%20clean%20composition&width=1600&height=600&seq=about-hero&orientation=landscape";

const values = [
  { icon: "ri-compass-3-line", title: "网站定位", text: "一份面向所有动漫爱好者的《秘密教学动漫》剧情指南，把散落在各处的剧情信息、分集要点与彩蛋集中整理到一个地方。" },
  { icon: "ri-lightbulb-flash-line", title: "发展理念", text: "用清晰的整理降低理解门槛，让每个人都能轻松跟上剧情，而不是在各种帖子之间来回翻找、越看越乱。" },
  { icon: "ri-edit-2-line", title: "内容原则", text: "内容以剧情讨论与资料整理为主，坚持客观、清晰、可持续更新。涉及结局等内容都会明确标注剧透提醒。" },
  { icon: "ri-team-line", title: "持续共建", text: "欢迎每一位读者补充遗漏的细节与彩蛋。你的每一条反馈，都会让这份指南变得更好。" },
];

const reviews = [
  { quote: "以前追到一半就忘了前面的伏笔，现在直接来这查分集，思路一下就顺了。", name: "追番的柚子" },
  { quote: "结局解析写得克制又到位，回看彩蛋那段真的说到我心坎里了。", name: "二刷选手" },
  { quote: "整理得很干净，想看哪话点哪话，不用再去评论区捞剧情了。", name: "阿默" },
];

export default function About() {
  usePageTitle("关于本站");
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://mysteryplot.github.io/" },
          { "@type": "ListItem", position: 2, name: "关于本站" },
        ],
      },
      {
        "@type": "AboutPage",
        name: "关于秘密教学动漫指南",
        url: "https://mysteryplot.github.io/about",
        description: "秘密教学动漫剧情指南的网站定位、发展理念与内容原则。",
      },
    ],
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SeoScript data={jsonLd} />
      <AnimeNavbar />
      <main className="flex-1">
        <PageHero
          tag={siteName}
          title="关于本站"
          desc="了解这份《秘密教学动漫》剧情指南的来龙去脉——我们想做什么、坚持什么，以及如何一起把它做得更好。"
          bg={heroBg}
        />

        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <nav className="mb-8 flex items-center gap-1 text-sm" aria-label="面包屑">
            <Link to="/" className="text-foreground-500 hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-800">关于本站</span>
          </nav>

          <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 lg:col-span-2">
              <span className="text-sm font-medium text-primary-600">ABOUT</span>
              <h2 className="font-heading mt-1 text-2xl text-foreground-900">一份替你整理好的剧情指南</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                {siteName}诞生于一个很简单的念头：追番的快乐，不该被「剧情散落在各处、越查越乱」的烦恼冲淡。
                我们想把《秘密教学动漫》的剧情主线、分集要点、角色关系与结局彩蛋，全部整理成一份清晰、可查、可持续更新的指南。
              </p>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                从剧情概览到逐集解读，从角色图鉴到原作细节对比，我们希望这里能成为你追这部作品时最顺手的一块「地图」。
                无论是第一次入坑，还是二刷找彩蛋，都能在这里找到想要的答案。
              </p>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-6">
              <span className="text-sm font-medium text-accent-800">WANTED</span>
              <h2 className="mt-1 text-lg text-accent-900">想和你一起完善</h2>
              <p className="mt-3 text-sm leading-6 text-accent-800">
                如果你发现了我们没有写到的细节或彩蛋，欢迎通过首页的联系表单告诉我们。你的一句话，可能就是下一位读者眼中的惊喜。
              </p>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">我们相信的事</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v) => (
                <article key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 text-base font-medium text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{v.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">读者怎么说</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              {reviews.map((r) => (
                <figure key={r.name} className="rounded-lg bg-background-50 p-5">
                  <i className="ri-double-quotes-l text-primary-300 text-2xl"></i>
                  <blockquote className="mt-2 text-sm leading-6 text-foreground-700">{r.quote}</blockquote>
                  <figcaption className="mt-3 text-sm text-foreground-500">— {r.name}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        </div>
      </main>
      <AnimeFooter />
    </div>
  );
}